import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { VideoCard, VideoPlayerModal } from '../components/VideoPlayer';
import { 
  TrendingUp, 
  Shield, 
  Users, 
  ChevronRight, 
  ChevronLeft,
  Play,
  CheckCircle,
  ArrowRight,
  Wallet,
  BarChart3,
  Lock,
  Globe,
  Mail,
  MapPin,
  Loader2,
  Sparkles,
  Zap,
  Star,
  ChevronDown,
  Menu,
  X,
  Quote,
  User,
  HelpCircle
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Hero images
const HERO_IMAGES = {
  phoneMain: 'https://images.unsplash.com/photo-1565268878573-5f968e45c9fe?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  cards: 'https://images.pexels.com/photos/13807424/pexels-photo-13807424.jpeg?auto=compress&cs=tinysrgb&w=800',
  lifestyle: 'https://images.pexels.com/photos/3785263/pexels-photo-3785263.jpeg?auto=compress&cs=tinysrgb&w=800',
  growth: 'https://images.unsplash.com/photo-1579227114496-27346f474519?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
  dashboard: 'https://images.unsplash.com/photo-1660970781103-ba6749cb9ce3?crop=entropy&cs=srgb&fm=jpg&w=800&q=85'
};

// Brand Colors
const COLORS = {
  base: '#1c262d',
  baseDark: '#0d1218',
  accent: '#93a5af',      // Primary accent (gray-blue)
  accentSecondary: '#93a5af', // Secondary accent (blue-gray to match builder)
};

// Icon mapping for dynamic icons
const iconMap = {
  TrendingUp,
  Shield,
  Wallet,
  BarChart3,
  Users,
  Lock,
  Globe,
  CheckCircle,
  Zap,
  Sparkles
};

// Testimonials Image Carousel Component
const TestimonialsCarousel = ({ images, accentColor = '#3a7189' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Default testimonial images if none provided
  const defaultImages = [
    { id: 1, url: 'https://customer-assets.emergentagent.com/job_65a6b6b5-ec09-4bb0-bfe4-b5ab612dfe11/artifacts/7marv7m5_testimonio2.png', alt: 'Testemunho 1' },
    { id: 2, url: 'https://customer-assets.emergentagent.com/job_65a6b6b5-ec09-4bb0-bfe4-b5ab612dfe11/artifacts/t341kzky_testimonio3.png', alt: 'Testemunho 2' },
    { id: 3, url: 'https://customer-assets.emergentagent.com/job_65a6b6b5-ec09-4bb0-bfe4-b5ab612dfe11/artifacts/4q16powd_testimonio4.png', alt: 'Testemunho 3' },
    { id: 4, url: 'https://customer-assets.emergentagent.com/job_65a6b6b5-ec09-4bb0-bfe4-b5ab612dfe11/artifacts/0mj86544_testimonio5.png', alt: 'Testemunho 4' },
    { id: 5, url: 'https://customer-assets.emergentagent.com/job_65a6b6b5-ec09-4bb0-bfe4-b5ab612dfe11/artifacts/2mkh4a4f_testimonio6.png', alt: 'Testemunho 5' },
  ];
  
  // Ensure images is always an array
  const safeImages = Array.isArray(images) ? images : [];
  const displayImages = safeImages.length > 0 ? safeImages : defaultImages;
  const totalSlides = displayImages.length;
  
  // Smooth transition handler
  const smoothTransition = (newIndex) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(newIndex);
    setTimeout(() => setIsTransitioning(false), 800);
  };
  
  // Auto-play effect
  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 3) return;
    
    const interval = setInterval(() => {
      smoothTransition((currentIndex + 1) % totalSlides);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides, currentIndex, isTransitioning]);
  
  const goToPrevious = () => {
    setIsAutoPlaying(false);
    smoothTransition(currentIndex === 0 ? totalSlides - 1 : currentIndex - 1);
  };
  
  const goToNext = () => {
    setIsAutoPlaying(false);
    smoothTransition((currentIndex + 1) % totalSlides);
  };

  if (displayImages.length === 0) {
    return (
      <div className="text-center py-12 text-[#93a5af]">
        <p>Nenhum testemunho disponível</p>
      </div>
    );
  }

  // Get visible images - 1 on mobile, 3 on desktop
  const getVisibleImages = (count = 3) => {
    const visible = [];
    for (let i = 0; i < count; i++) {
      const index = (currentIndex + i) % totalSlides;
      visible.push({ ...displayImages[index], originalIndex: index });
    }
    return visible;
  };

  return (
    <>
      <div className="relative">
        {/* Main Carousel Container */}
        <div className="relative overflow-hidden px-4 md:px-14">
          {/* Mobile: 1 image */}
          <div className="md:hidden">
            <div 
              className="flex"
              style={{
                transition: 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
              }}
            >
              {getVisibleImages(1).map((image, idx) => (
                <div 
                  key={`mobile-${image.id}-${currentIndex}-${idx}`}
                  className="w-full flex-shrink-0"
                  style={{
                    opacity: isTransitioning ? 0.7 : 1,
                    transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
                    transition: 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
                  }}
                >
                  <div 
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img 
                      src={image.url}
                      alt={image.alt || `Testemunho ${image.originalIndex + 1}`}
                      className="w-full h-auto rounded-2xl shadow-xl shadow-black/20 border border-white/10"
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop: 3 images */}
          <div 
            className="hidden md:flex gap-6"
            style={{
              transition: 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
          >
            {getVisibleImages(3).map((image, idx) => (
              <div 
                key={`desktop-${image.id}-${currentIndex}-${idx}`}
                className="flex-1 min-w-0"
                style={{
                  opacity: isTransitioning ? 0.7 : 1,
                  transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
                  transition: 'all 0.8s cubic-bezier(0.25, 0.1, 0.25, 1)',
                }}
              >
                <div 
                  className="relative group cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img 
                    src={image.url}
                    alt={image.alt || `Testemunho ${image.originalIndex + 1}`}
                    className="w-full h-auto rounded-2xl shadow-xl shadow-black/20 border border-white/10 transition-all duration-500 ease-out group-hover:scale-[1.03] group-hover:shadow-2xl group-hover:border-white/20"
                    loading="lazy"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/10 transition-all duration-500 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-90 group-hover:scale-100">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  {/* Glow effect */}
                  <div 
                    className="absolute -inset-2 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10 blur-2xl"
                    style={{ background: `${accentColor}20` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Navigation Arrows */}
        <button
          onClick={goToPrevious}
          disabled={isTransitioning}
          className="absolute left-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-10"
          aria-label="Anterior"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={goToNext}
          disabled={isTransitioning}
          className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-10"
          aria-label="Próximo"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        
        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {displayImages.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsAutoPlaying(false);
                smoothTransition(index);
              }}
              disabled={isTransitioning}
              className={`h-2 rounded-full transition-all duration-500 ${
                index === currentIndex 
                  ? 'w-8 bg-white' 
                  : 'w-2 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
          style={{
            animation: 'fadeIn 0.3s ease-out',
          }}
        >
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes scaleIn {
              from { opacity: 0; transform: scale(0.9); }
              to { opacity: 1; transform: scale(1); }
            }
          `}</style>
          
          {/* Close button */}
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-10"
          >
            <X className="w-6 h-6" />
          </button>
          
          {/* Image */}
          <img 
            src={selectedImage.url}
            alt={selectedImage.alt}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-2xl shadow-2xl"
            style={{
              animation: 'scaleIn 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Navigation in modal */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentIdx = displayImages.findIndex(img => img.id === selectedImage.id);
              const prevIdx = currentIdx === 0 ? displayImages.length - 1 : currentIdx - 1;
              setSelectedImage(displayImages[prevIdx]);
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentIdx = displayImages.findIndex(img => img.id === selectedImage.id);
              const nextIdx = (currentIdx + 1) % displayImages.length;
              setSelectedImage(displayImages[nextIdx]);
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </>
  );
};

// Default content (fallback) - Portuguese (Portugal)
const DEFAULT_CONTENT = {
  styles: {
    primaryColor: COLORS.accent,
    secondaryColor: COLORS.accentSecondary,
    heroBackground: COLORS.baseDark,
    sectionBackground: COLORS.base,
    textColor: '#FFFFFF',
    accentColor: COLORS.accentSecondary
  },
  header: {
    menuItems: [
      { label: "Como Funciona", href: "#como-funciona" },
      { label: "Benefícios", href: "#beneficios" },
      { label: "Testemunhos", href: "#testimonios" },
      { label: "Contacto", href: "#contacto" }
    ],
    loginText: "Iniciar Sessão",
    registerText: "Criar Conta"
  },
  hero: {
    badge: "Smart Global Financial Insights",
    title: "O Seu Dinheiro,",
    titleHighlight: "Sem Limites",
    subtitle: "Junte-se a milhares de investidores que confiam em nós. Rendimentos atrativos, gestão profissional e total transparência.",
    ctaPrimary: "Começar Agora",
    ctaSecondary: "Saber Mais",
    backgroundImage: "",
    stats: [
      { value: "4%", label: "Rendimento mensal" },
      { value: "100+", label: "Clientes ativos" },
      { value: "24/7", label: "Acesso total" }
    ]
  },
  howItWorks: {
    title: "Investir Nunca Foi Tão Fácil",
    subtitle: "Comece em minutos, não em dias",
    steps: [
      { step: "01", title: "Crie a Sua Conta", description: "Registo rápido e 100% digital. Sem burocracia." },
      { step: "02", title: "Deposite Fundos", description: "Transferência bancária segura e verificada." },
      { step: "03", title: "Veja o Seu Dinheiro Crescer", description: "Rendimentos mensais diretamente na sua conta." }
    ]
  },
  benefits: {
    title: "Tudo o Que Precisa",
    subtitle: "Uma plataforma completa para os seus investimentos",
    items: [
      { icon: "TrendingUp", title: "Rendimentos Atrativos", description: "Taxas competitivas do mercado com pagamentos mensais garantidos." },
      { icon: "Shield", title: "Máxima Segurança", description: "O seu capital protegido com os mais altos padrões de segurança." },
      { icon: "Wallet", title: "Gestão Simples", description: "Plataforma intuitiva para controlar os seus investimentos." },
      { icon: "BarChart3", title: "Transparência Total", description: "Acesso em tempo real a todos os seus movimentos." }
    ],
    features: ["100% digital", "Sem comissões ocultas", "Levantamentos flexíveis", "Relatórios detalhados", "Suporte dedicado", "App móvel"]
  },
  testimonials: {
    title: "O Que Dizem os Nossos Clientes",
    subtitle: "Milhares de investidores confiam em nós",
    items: [
      { name: "Maria Silva", role: "Empresária", rating: 5, text: "Excelente plataforma. Os rendimentos são consistentes e o serviço ao cliente é excecional.", avatar: "" },
      { name: "Carlos Santos", role: "Engenheiro", rating: 5, text: "Há anos que procurava uma opção segura para investir. A SGFI Capital superou as minhas expectativas.", avatar: "" },
      { name: "Ana Ferreira", role: "Médica", rating: 5, text: "A transparência e facilidade de uso convenceram-me desde o primeiro dia. Muito recomendável.", avatar: "" }
    ]
  },
  videos: {
    title: "Conheça a Nossa Plataforma",
    subtitle: "Descubra como funciona",
    items: [
      { id: 1, title: "Como Funciona", description: "Guia completo de investimento.", embedUrl: "", placeholder: true },
      { id: 2, title: "Abertura de Conta", description: "Processo passo a passo.", embedUrl: "", placeholder: true },
      { id: 3, title: "Segurança", description: "Conheça as nossas medidas.", embedUrl: "", placeholder: true }
    ]
  },
  cta: {
    title: "Pronto Para Começar?",
    subtitle: "Abra a sua conta hoje e comece a ver o seu dinheiro crescer.",
    buttonText: "Criar Conta Grátis"
  },
  contact: {
    title: "Tem Dúvidas?",
    subtitle: "A nossa equipa está disponível para o ajudar.",
    email: "capitalsgfi@gmail.com",
    location: "Lisboa, Portugal",
    legalTitle: "Informação Legal",
    legalText: "SGFI Capital - Smart Global Financial Insights. Todos os investimentos envolvem risco."
  },
  faq: {
    title: "Perguntas Frequentes",
    subtitle: "Encontre respostas às questões mais comuns sobre a nossa plataforma.",
    items: [
      {
        id: "faq1",
        question: "Como posso abrir uma conta na SGFI Capital?",
        answer: "Para abrir uma conta, basta clicar em 'Criar Conta', preencher o formulário de registo com os seus dados pessoais e carregar um documento de identificação. A sua conta será analisada e aprovada em até 48 horas úteis."
      },
      {
        id: "faq2",
        question: "Qual é o montante mínimo para investir?",
        answer: "O montante mínimo para começar a investir na SGFI Capital é de €1.000. Este valor permite-lhe aceder a todas as funcionalidades da plataforma e começar a gerar rendimentos mensais."
      },
      {
        id: "faq3",
        question: "Como são calculados os rendimentos?",
        answer: "Os rendimentos são calculados mensalmente com base no saldo da sua conta e na taxa de rendimento acordada. Os depósitos feitos durante o mês são calculados proporcionalmente aos dias restantes do mês."
      },
      {
        id: "faq4",
        question: "Como posso levantar os meus fundos?",
        answer: "Pode solicitar um levantamento a qualquer momento através da sua área de cliente. Os levantamentos são processados em até 5 dias úteis e transferidos para a conta bancária registada no seu perfil."
      },
      {
        id: "faq5",
        question: "Os meus dados estão seguros?",
        answer: "Sim, utilizamos encriptação de última geração para proteger todos os seus dados. Cumprimos integralmente o RGPD e as normas de segurança bancária europeias."
      }
    ]
  },
  visibility: {
    hero: true,
    howItWorks: true,
    benefits: true,
    testimonials: true,
    videos: true,
    faq: true,
    cta: true,
    contact: true
  }
};

// FAQ Item component with accordion-style expand/collapse
const FAQItem = ({ question, answer, accentColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div 
      className="border border-white/10 rounded-2xl overflow-hidden transition-all duration-300"
      style={{ backgroundColor: isOpen ? '#1c262d' : 'transparent' }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
        data-testid="faq-item-toggle"
      >
        <span className="text-lg font-medium text-white pr-4">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: accentColor || '#3a7189' }}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
      >
        <div className="px-6 pb-6 text-[#93a5af] leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
};

// Scroll animation hook
const useScrollAnimation = (startVisible = false) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(startVisible);
  
  useEffect(() => {
    if (startVisible) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: '100px' }
    );
    
    const timeout = setTimeout(() => {
      if (ref.current) observer.observe(ref.current);
    }, 100);
    
    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [startVisible]);
  
  return [ref, isVisible];
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Determine dashboard route based on user role
  const getDashboardRoute = () => {
    if (!user) return '/login';
    return user.is_admin ? '/admin' : '/dashboard';
  };

  useEffect(() => {
    fetchContent();
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/content/landing`);
      if (response.data.content) {
        setContent({
          ...DEFAULT_CONTENT,
          ...response.data.content,
          styles: { ...DEFAULT_CONTENT.styles, ...(response.data.content.styles || {}) },
          hero: { ...DEFAULT_CONTENT.hero, ...(response.data.content.hero || {}) },
          testimonials: { ...DEFAULT_CONTENT.testimonials, ...(response.data.content.testimonials || {}) },
          videos: { ...DEFAULT_CONTENT.videos, ...(response.data.content.videos || {}) }
        });
      }
    } catch (error) {
      // Error silenced: 'Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || TrendingUp;
    return Icon;
  };

  // Animation refs
  const [heroRef, heroVisible] = useScrollAnimation(true);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1218] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3a7189] animate-spin" />
      </div>
    );
  }

  // Get dynamic styles from content
  const styles = content?.styles || {};
  const accentColor = styles.accentColor || '#3a7189';
  const primaryColor = styles.primaryColor || '#93a5af';
  const secondaryColor = styles.secondaryColor || '#3a7189';
  const heroBackground = styles.heroBackground || '#0d1218';
  const sectionBackground = styles.sectionBackground || '#1c262d';

  // CSS variables for dynamic styling
  const cssVariables = {
    '--accent-color': accentColor,
    '--primary-color': primaryColor,
    '--secondary-color': secondaryColor,
    '--hero-bg': heroBackground,
    '--section-bg': sectionBackground,
  };

  // Get header content
  const headerContent = content.header || DEFAULT_CONTENT.header;
  const menuItems = Array.isArray(headerContent?.menuItems) ? headerContent.menuItems : DEFAULT_CONTENT.header.menuItems;

  return (
    <div className="min-h-screen" style={{ ...cssVariables, backgroundColor: heroBackground }} data-testid="landing-page">
      {/* Navigation - Glassmorphic Dark Style */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-[#0d1218]/95 backdrop-blur-xl border-b border-white/10' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="SGFI Capital" className="h-10 w-auto" />
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {menuItems.map((item, index) => (
                <a 
                  key={index}
                  href={item.href} 
                  className="text-[#93a5af] hover:text-white transition-colors font-medium"
                >
                  {item.label}
                </a>
              ))}
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <Button
                  onClick={() => navigate(getDashboardRoute())}
                  className="text-white rounded-full px-6 font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2"
                  style={{ backgroundColor: accentColor, boxShadow: `0 10px 15px -3px ${accentColor}40` }}
                  data-testid="user-dashboard-btn"
                >
                  <User className="w-4 h-4" />
                  Olá, {user.name?.split(' ')[0] || 'Utilizador'}!
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    className="hover:text-white hover:bg-white/10 rounded-full px-6 font-medium"
                    style={{ color: primaryColor }}
                    data-testid="login-btn"
                  >
                    {headerContent.loginText || 'Iniciar Sessão'}
                  </Button>
                  <Button
                    onClick={() => navigate('/register')}
                    className="text-white rounded-full px-6 font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                    style={{ backgroundColor: accentColor, boxShadow: `0 10px 15px -3px ${accentColor}40` }}
                    data-testid="register-btn"
                  >
                    {headerContent.registerText || 'Criar Conta'}
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-[#93a5af]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0d1218] border-t border-white/10 py-4 px-4">
            <div className="flex flex-col gap-4">
              {menuItems.map((item, index) => (
                <a 
                  key={index}
                  href={item.href} 
                  className="text-[#93a5af] font-medium py-2" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <hr className="border-white/10" />
              {user ? (
                <Button
                  onClick={() => { navigate(getDashboardRoute()); setMobileMenuOpen(false); }}
                  className="text-white rounded-full w-full flex items-center justify-center gap-2"
                  style={{ backgroundColor: accentColor }}
                  data-testid="mobile-user-dashboard-btn"
                >
                  <User className="w-4 h-4" />
                  Olá, {user.name?.split(' ')[0] || 'Utilizador'}!
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="rounded-full w-full border-white/20 text-white hover:bg-white/10"
                  >
                    {headerContent.loginText || 'Iniciar Sessão'}
                  </Button>
                  <Button
                    onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                    className="text-white rounded-full w-full"
                    style={{ backgroundColor: accentColor }}
                  >
                    {headerContent.registerText || 'Criar Conta'}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Dynamic Colors */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden"
        style={{ backgroundColor: heroBackground }}
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] rounded-full blur-3xl" style={{ backgroundColor: `${accentColor}25` }} />
          <div className="absolute bottom-1/4 -right-1/4 w-[600px] h-[600px] rounded-full blur-3xl" style={{ backgroundColor: `${primaryColor}15` }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl" style={{ backgroundColor: `${sectionBackground}80` }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className={`transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8" style={{ backgroundColor: `${accentColor}20`, borderColor: `${accentColor}40`, borderWidth: 1 }}>
                <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
                <span className="text-sm font-medium" style={{ color: primaryColor }}>{content.hero?.badge}</span>
              </div>
              
              {/* Title */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight mb-6">
                {content.hero?.title}
                <span className="block text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${accentColor}, ${primaryColor})` }}>
                  {content.hero?.titleHighlight}
                </span>
              </h1>
              
              <p className="text-xl mb-10 max-w-lg leading-relaxed" style={{ color: primaryColor }}>
                {content.hero?.subtitle}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate('/register')}
                  className="text-white rounded-full px-8 h-14 text-lg font-semibold shadow-xl transition-all hover:scale-105"
                  style={{ backgroundColor: accentColor, boxShadow: `0 20px 25px -5px ${accentColor}40` }}
                  data-testid="hero-cta"
                >
                  {content.hero?.ctaPrimary}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' })}
                  className="text-white rounded-full px-8 h-14 text-lg font-medium transition-all"
                  style={{ borderColor: `${primaryColor}40`, borderWidth: 2 }}
                >
                  {content.hero?.ctaSecondary}
                </Button>
              </div>

            </div>

            {/* Right - Hero Image */}
            <div className={`relative transition-all duration-1000 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative">
                {/* Floating phone mockup */}
                <div className="relative z-10 mx-auto max-w-md">
                  <div className="relative rounded-[3rem] overflow-hidden shadow-2xl shadow-[#2e8036]/20 bg-gradient-to-b from-[#1c262d] to-[#0d1218] p-2">
                    <img 
                      src={HERO_IMAGES.dashboard}
                      alt="App Dashboard"
                      className="rounded-[2.5rem] w-full"
                    />
                  </div>
                  
                  {/* Floating cards */}
                  <div className="absolute -left-16 top-1/4 bg-[#1c262d] border border-[#2e8036]/30 rounded-2xl shadow-xl shadow-[#2e8036]/10 p-4 animate-float">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#2e8036]/20 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-[#3a7189]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#93a5af]/70">Rendimiento</p>
                        <p className="text-xl font-bold text-white">+4.0%</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -right-12 bottom-1/3 bg-[#1c262d] border border-[#93a5af]/20 rounded-2xl shadow-xl p-4 animate-float-delayed">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#93a5af]/20 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-[#93a5af]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#93a5af]/70">Seguro</p>
                        <p className="text-lg font-bold text-white">100%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-20 grid grid-cols-3 gap-8 max-w-3xl mx-auto">
            {(Array.isArray(content.hero?.stats) ? content.hero.stats : []).map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl sm:text-5xl font-bold text-white">{stat.value}</p>
                <p className="text-[#93a5af]/70 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Dynamic Colors */}
      <section id="como-funciona" className="py-24" style={{ backgroundColor: sectionBackground }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              {content.howItWorks?.title}
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: primaryColor }}>
              {content.howItWorks?.subtitle}
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-6">
            {(Array.isArray(content.howItWorks?.steps) ? content.howItWorks.steps : []).map((item, index) => (
              <div 
                key={index}
                className="group rounded-3xl p-8 border border-white/5 hover:shadow-xl transition-all duration-500 animate-fade-in"
                style={{ backgroundColor: heroBackground, animationDelay: `${index * 150}ms` }}
              >
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl text-white text-xl font-bold mb-6 group-hover:scale-110 transition-transform shadow-lg" style={{ backgroundColor: accentColor, boxShadow: `0 10px 15px -3px ${accentColor}40` }}>
                  {item.step}
                </span>
                <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                <p style={{ color: primaryColor }} className="leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features - Dynamic Colors */}
      <section id="beneficios" className="py-24" style={{ backgroundColor: heroBackground }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              {content.benefits?.title}
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: primaryColor }}>
              {content.benefits?.subtitle}
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(Array.isArray(content.benefits?.items) ? content.benefits.items : []).map((benefit, index) => {
              const Icon = getIcon(benefit.icon);
              return (
                <div 
                  key={index}
                  className="group rounded-3xl p-8 border border-white/5 hover:shadow-xl transition-all duration-500 animate-fade-in"
                  style={{ backgroundColor: sectionBackground, animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all" style={{ backgroundColor: `${accentColor}15` }}>
                    <Icon className="w-7 h-7 transition-colors" style={{ color: accentColor }} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                  <p style={{ color: primaryColor }}>{benefit.description}</p>
                </div>
              );
            })}
          </div>

          {/* Features Checklist */}
          <div className="mt-16 rounded-3xl p-8 md:p-12" style={{ background: `linear-gradient(to right, ${accentColor}, ${accentColor}dd)` }}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(Array.isArray(content.benefits?.features) ? content.benefits.features : []).map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                  <span className="text-white">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonios" className="py-24" style={{ backgroundColor: heroBackground }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              {content.testimonials?.title}
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: primaryColor }}>
              {content.testimonials?.subtitle}
            </p>
          </div>

          {/* Testimonials Image Carousel */}
          <TestimonialsCarousel 
            images={content.testimonials?.images || []}
            accentColor={accentColor}
          />
        </div>
      </section>

      {/* Videos Section */}
      <section id="videos" className="py-24" style={{ backgroundColor: sectionBackground }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              {content.videos?.title}
            </h2>
            <p className="text-xl text-[#93a5af] max-w-2xl mx-auto">
              {content.videos?.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {(Array.isArray(content.videos?.items) ? content.videos.items : []).map((video, index) => {
              // Convert YouTube URL to embed format for iframe fallback
              const getEmbedUrl = (url) => {
                if (!url) return null;
                if (url.includes('youtube.com/embed/')) return url;
                const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
                if (youtubeMatch) {
                  return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
                }
                const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
                if (vimeoMatch) {
                  return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
                }
                return url;
              };
              
              const embedUrl = getEmbedUrl(video.embedUrl);
              const isLocalVideo = video.videoUrl && !video.videoUrl.includes('youtube') && !video.videoUrl.includes('vimeo');
              
              return (
                <VideoCard
                  key={video.id || index}
                  video={{
                    ...video,
                    embedUrl: embedUrl,
                    videoUrl: video.videoUrl
                  }}
                  onClick={() => {
                    if (isLocalVideo) {
                      setSelectedVideo(video);
                    }
                  }}
                  accentColor={accentColor}
                  primaryColor={primaryColor}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Player Modal */}
      <VideoPlayerModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoUrl={selectedVideo?.videoUrl}
        title={selectedVideo?.title}
      />

      {/* FAQ Section */}
      {content.visibility?.faq !== false && (
        <section id="faq" className="py-24" style={{ backgroundColor: heroBackground }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#2e8036]/10 mb-6">
                <HelpCircle className="w-8 h-8 text-[#3a7189]" />
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
                {content.faq?.title || "Perguntas Frequentes"}
              </h2>
              <p className="text-xl text-[#93a5af] max-w-2xl mx-auto">
                {content.faq?.subtitle || "Encontre respostas às questões mais comuns."}
              </p>
            </div>

            <div className="space-y-4">
              {(Array.isArray(content.faq?.items) ? content.faq.items : []).map((faq, index) => (
                <FAQItem 
                  key={faq.id || index} 
                  question={faq.question} 
                  answer={faq.answer}
                  accentColor={accentColor}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Dynamic Gradient */}
      <section className="py-24" style={{ background: `linear-gradient(to right, ${accentColor}, ${accentColor}cc)` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fade-in">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            {content.cta?.title}
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            {content.cta?.subtitle}
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-white text-black rounded-full px-10 h-16 text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105 hover:bg-white hover:text-black hover:shadow-white/20"
            data-testid="cta-register"
          >
            {content.cta?.buttonText}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24" style={{ backgroundColor: heroBackground }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">
                {content.contact?.title}
              </h2>
              <p className="text-xl mb-10" style={{ color: primaryColor }}>
                {content.contact?.subtitle}
              </p>
              
              <div className="space-y-6">
                <a 
                  href={`mailto:${content.contact?.email}`}
                  className="flex items-center gap-4 p-4 rounded-2xl border border-white/5 transition-colors group"
                  style={{ backgroundColor: sectionBackground }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#2e8036]/20 flex items-center justify-center group-hover:bg-[#2e8036]/30 transition-colors">
                    <Mail className="w-6 h-6 text-[#3a7189]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#93a5af]/70">Email</p>
                    <p className="text-lg font-semibold text-white">{content.contact?.email}</p>
                  </div>
                </a>
                
                <div className="flex items-center gap-4 p-4 rounded-2xl bg-[#1c262d] border border-white/5">
                  <div className="w-14 h-14 rounded-2xl bg-[#2e8036]/20 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-[#3a7189]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#93a5af]/70">Localização</p>
                    <p className="text-lg font-semibold text-white">{content.contact?.location}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1c262d] border border-white/5 rounded-3xl p-8 md:p-10">
              <h3 className="text-2xl font-bold text-white mb-6">{content.contact?.legalTitle}</h3>
              <div className="space-y-4 text-[#93a5af]">
                <p>{content.contact?.legalText}</p>
                <p className="text-sm">© {new Date().getFullYear()} SGFI Capital. Todos os direitos reservados.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0d1218] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-8 w-auto" />
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
              <a href="/privacy" className="text-[#93a5af]/70 hover:text-white text-sm transition-colors">Política de Privacidade</a>
              <a href="/terms" className="text-[#93a5af]/70 hover:text-white text-sm transition-colors">Termos e Condições</a>
              <a href="/cookies" className="text-[#93a5af]/70 hover:text-white text-sm transition-colors">Política de Cookies</a>
            </div>
            <p className="text-[#93a5af]/50 text-sm">
              © {new Date().getFullYear()} SGFI Capital
            </p>
          </div>
        </div>
      </footer>

      {/* Custom styles for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 5s ease-in-out infinite;
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}
